<template>
  <div class="bg-grey-1 q-pa-lg">
    <q-card>
      <q-card-section>
        <h2 class="text-h6 q-mb-md q-mt-none">
          <q-icon name="fa fa-download" class="on-left" />
          <span class="text-weight-regular">Step 1:</span> Export & Download Your Posts
        </h2>
        <div class="text-body1 q-mb-md">
          The first step of getting your posts into Private Footprint
          is exporting them from {{ platform.name }}.
        </div>
        <div class="text-body1 q-mb-md">
          Get step by step instructions on how to export your
          posts from {{ platform.name }} by selecting an option below.
        </div>
        <instructions :platform="platform.type" />
      </q-card-section>
    </q-card>
    <q-card class="q-mt-lg">
      <q-card-section>
        <h2 class="text-h6 q-mb-md q-mt-none">
          <q-icon name="fa fa-upload" class="on-left" />
          <span class="text-weight-regular">Step 2:</span> Upload Your Posts
        </h2>
        <div class="text-body1 q-mb-md">
          When you have downloaded your {{ platform.name }} posts, please upload them here.
        </div>
        <file-list
          :files="uploadedFiles"
          :max-files="1"
          upload-placeholder="Drag .zip file here or click to upload"
          :allowed-extensions="['zip']"
          :validate="validateFile"
          class="q-mt-lg"
          can-upload
          can-delete
          @update:files="onFilesChanged"
        />
        <message v-if="error" type="negative" class="q-my-md">
          {{ error }}
        </message>
        <async-error-message :task="task" class="q-my-md" />
        <loader :task="task" class="q-my-md" />
      </q-card-section>
    </q-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Loader from '@/components/loading/AsyncLinearProgress.vue';
import AsyncErrorMessage from '@/components/message/AsyncErrorMessage.vue';
import FileList from '@/media/FileList.vue';
import Message from '@/components/message/Message.vue';
import { Platform } from '@/social/types';
import { PostImport, UploadedFile } from '@/type';
import Instructions from '../Instructions.vue';
import { create } from '..';
import { analyzeFile } from '.';

interface Data {
  error: string|null,
  uploadedFiles: UploadedFile[],
  task: Promise<PostImport>|null
}

export default defineComponent({
  emits: ['created'],
  components: {
    Message,
    AsyncErrorMessage,
    FileList,
    Loader,
    Instructions,
  },
  props: {
    platform: {
      type: Object as () => Platform,
      required: true,
    },
    getAccountId: {
      type: Function as PropType<() => Promise<number>>,
      required: true,
    },
  },
  data(): Data {
    return {
      error: null,
      uploadedFiles: [],
      task: null,
    };
  },
  methods: {
    async validateFile(file: File): Promise<boolean> {
      this.error = null;
      if (!file.name.endsWith('.zip')) {
        this.error = 'Please upload a .zip file.';
        return false;
      }
      // Chrome cannot handle files bigger than 2GB so skip client side validation
      if (file.size > 2000000000) return true;
      const fileInfo = await analyzeFile(file);
      if (fileInfo === null) {
        this.error = `The file you uploaded was not what we expected. Please upload your ${this.platform.name} file.`;
      } else if (fileInfo.platform !== this.platform.type) {
        this.error = `The file you uploaded was for ${fileInfo.platform} but it should be your ${this.platform.name} file.`;
      } else if (fileInfo.error) {
        this.error = fileInfo.error;
      }
      return (this.error === null);
    },
    onFilesChanged(files: UploadedFile[]) {
      this.task = null;
      this.uploadedFiles = files;
      if (files.length === 1) {
        this.create(files[0]);
      }
    },
    create(uploadedFile: UploadedFile) {
      this.task = this
        .getAccountId()
        .then(socialAccountId => create(socialAccountId, uploadedFile.id));
      this.task.then((postImport) => {
        this.$emit('created', postImport);
      });
    },
  },
});
</script>
